/**
 * Runtime environment configuration
 *
 * This module provides environment variables that are injected at runtime
 * (not build time) to keep Docker images environment-agnostic.
 *
 * Values come from window.__ENV__, which is populated by /env.js. Deployed,
 * that file is a ConfigMap mount (.k8s/frontend/configmap.yaml); see
 * docs/how-to/frontend-runtime-config.md.
 */

interface RuntimeEnv {
  VITE_FD_BASE_URL: string;
  VITE_SSU_ENDPOINT: string;
  VITE_OKTA_ISSUER: string;
  VITE_OKTA_CLIENT_ID: string;
  VITE_DEV_MODE: string;
  VITE_FULLSTORY_ORG_ID: string;
  VITE_ROLLBAR_TOKEN: string;
  VITE_ROLLBAR_ENVIRONMENT: string;
  VITE_CODE_VERSION: string;
  VITE_SSU_PROFILE_URL: string;
  VITE_APEX_ORIGIN: string;
  VITE_APEX_SIM_COMPONENT_PATH: string;
  VITE_APEX_API_BASE_URL: string;
}

declare global {
  interface Window {
    __ENV__?: Partial<RuntimeEnv>;
  }
}

function getEnv(key: keyof RuntimeEnv, fallback: string = ''): string {
  // Runtime config takes precedence (production)
  if (window.__ENV__?.[key]) {
    return window.__ENV__[key] as string;
  }
  // Fall back to Vite env vars (local development)
  return (import.meta.env[key] as string) || fallback;
}

export const env = {
  FD_BASE_URL: getEnv('VITE_FD_BASE_URL'),
  SSU_ENDPOINT: getEnv('VITE_SSU_ENDPOINT'),
  OKTA_ISSUER: getEnv('VITE_OKTA_ISSUER'),
  OKTA_CLIENT_ID: getEnv('VITE_OKTA_CLIENT_ID'),
  /** Enable dev mode toggle on course detail pages. Defaults to true in local development. */
  DEV_MODE: getEnv('VITE_DEV_MODE', import.meta.env.DEV ? 'true' : 'false') === 'true',
  /** FullStory org ID. Leave empty to disable FullStory (e.g. local dev). */
  FULLSTORY_ORG_ID: getEnv('VITE_FULLSTORY_ORG_ID'),
  /** Rollbar `halo-frontend` client token (post_client_item). Leave empty to disable error reporting. */
  ROLLBAR_TOKEN: getEnv('VITE_ROLLBAR_TOKEN'),
  /** Environment name Rollbar files items under, e.g. `qa` or `production`. */
  ROLLBAR_ENVIRONMENT: getEnv('VITE_ROLLBAR_ENVIRONMENT', 'local'),
  /** Pipeline build ID of the deployed image. Matches the version source maps are uploaded under. */
  CODE_VERSION: getEnv('VITE_CODE_VERSION', 'local'),
  /** Identity Hub profile page. Opens in new tab for users to update name/email. */
  SSU_PROFILE_URL: getEnv('VITE_SSU_PROFILE_URL'),
  /**
   * Origin serving Apex's simulation web component, e.g. `https://apex.example.com`.
   *
   * Unlike every other key here, the Apex keys deliberately carry NO literal
   * fallback: an unset value must stay empty so `apexSimulationLoader` can fail
   * visibly. A default would be the exact bug BPE-701 exists to prevent — a QA
   * build with a missing pipeline variable quietly loading production's Apex
   * bundle instead of erroring. Empty here is a diagnosable state, not a gap.
   */
  APEX_ORIGIN: getEnv('VITE_APEX_ORIGIN'),
  /**
   * Path to the simulation component on {@link APEX_ORIGIN}, e.g. `/learner-sim.js`.
   *
   * Split from the origin so the still-open pinning decision (a versioned path,
   * a proxy — Simulations PRD dependency 2) is a one-variable config edit rather
   * than a rewrite of every environment's full URL. No fallback, same reason as
   * APEX_ORIGIN.
   */
  APEX_SIM_COMPONENT_PATH: getEnv('VITE_APEX_SIM_COMPONENT_PATH'),
  /**
   * Base URL of Apex's API, handed to the simulation component so its own calls
   * reach Apex rather than Halo (BPE-722).
   *
   * A separate key from {@link APEX_ORIGIN} even where the two agree — on Apex's
   * dev deploy one origin serves both the bundle and the API. They need not: a
   * developer running Apex themselves gets the bundle from a Vite port and the
   * API from :5299, and reverse-proxying Apex's API under Halo's own origin
   * (HTS-6384) would change or remove this value. Deriving it from the origin
   * would encode today's coincidence as an assumption.
   *
   * No fallback, same reason as the two keys above — and one more: an empty
   * value is not inert here. Apex's generated client substitutes its own dev
   * default, `http://localhost:5299`, so a deployed learner's browser would
   * call a port on *their own machine* — refused, or answered by whatever they
   * happen to be running. Resolving it here makes that a named configuration
   * error instead.
   */
  APEX_API_BASE_URL: getEnv('VITE_APEX_API_BASE_URL'),
};
